import { Component, ComponentChildren } from 'preact'
import Categories from './categories'
import { ParentCategoryInfo } from '../mega-menu/Types'
import UnansweredTopics, { UnansweredTopicsProps } from './unanswered'
import RecentlyActiveTopics, { RecentlyActiveTopicsProps } from './recentlyActiveTopics'
import Phrase from '../../libs/translationsUtils/phrase'
import axios from 'axios'
import { Context, GetServerSideProps } from '../../server-middleware/context'
import {
  getById,
  getCategories,
  LegacyCategoryType,
  prepCategories
} from '../../server-middleware/categoryRepository'
import { z } from 'zod'
import Unified, { UnifiedTopic } from '../../services/unified'
import { ContentType } from '@insided/shared/utils/contentType'
import { TopicInfo, TopicType } from '../topic-list-view/TopicListItemView.sync'
import WidgetContainer, { WidgetContainerConfig, widgetContainerConfig } from '../widget-container'
import classnames from 'classnames'

type TabsType = Array<{
  enabled: boolean
  collapsed: boolean
  phrase?: string | undefined
  phrase_suffix: 'categories' | 'unanswered' | 'recent_active'
  class: string
}>

interface Props extends WidgetContainerConfig {
  tabs: TabsType
  parentCategories: ParentCategoryInfo[]
  collapsed: null | {
    [key: number]: boolean
  }
  displayUnreadPostCount: boolean
  unAnsweredTopics: UnansweredTopicsProps
  recentlyActiveTopics: RecentlyActiveTopicsProps
}

interface State {
  tabs: TabsType
  activeTab: number
  unreadTopicsAndRepliesCount: null | {
    [key: string]: {
      topicCount?: number
      postCount?: number
    }
  }
}

export const widgetName = 'category_and_topics_stream_only'

export const config = z.object({
  tabs: z.array(z.object({
    enabled: z.boolean(),
    collapsed: z.boolean(),
    phrase: z.union([z.string(), z.undefined()]),
    phrase_suffix: z.enum(['categories', 'unanswered', 'recent_active']),
    class: z.string()
  }))
}).merge(widgetContainerConfig)

export type CategoryAndContentStreamConfig = z.infer<typeof config>

function createUrl (unifiedTopic: UnifiedTopic, postId?: number): string {
  const url = `/fid-1/tid-${unifiedTopic.publicId}`
  if (postId) {
    return `${url}?postid=${postId}#post${postId}`
  }
  return url
}

function getType (categoryType: number|null, contentType: ContentType): TopicType {
  if (categoryType === LegacyCategoryType.ideation || contentType === 'idea') {
    return 'ideation'
  } else {
    return 'post'
  }
}

async function createTopicInfoFromUnifiedTopic (unifiedTopic: UnifiedTopic, unified: Unified, ctx: Context): Promise<TopicInfo> {
  const [
    firstPostAuthor,
    lastPostAuthor,
    category
  ] = await Promise.all([
    unified.api.getUser(unifiedTopic.author.id),
    unified.api.getUser(unifiedTopic.lastContributor.id),
    unifiedTopic.categoryId ? getById(ctx, parseInt(unifiedTopic.categoryId)) : null
  ])

  const numberOfLikes = unifiedTopic.contentType === 'idea' ? unifiedTopic.votes : unifiedTopic.likes
  let hasCurrentUserLiked = false
  if (ctx.userId) {
    hasCurrentUserLiked = (unifiedTopic.contentType === 'idea' ? unifiedTopic.voteSet : unifiedTopic.likeSet).includes(ctx.userId)
  }

  return {
    firstPost: {
      createdAt: new Date(unifiedTopic.createdAt).getTime(),
      url: createUrl(unifiedTopic),
      content: unifiedTopic.content,
      author: {
        id: firstPostAuthor.userid,
        userTitle: firstPostAuthor.usertitle,
        name: firstPostAuthor.username,
        avatar: firstPostAuthor.avatar,
        rank: firstPostAuthor.rank,
        url: `/members/m-${firstPostAuthor.userid}`
      }
    },
    relevantPost: {
      createdAt: new Date(unifiedTopic.createdAt).getTime(),
      url: createUrl(unifiedTopic),
      content: unifiedTopic.content,
      author: {
        id: firstPostAuthor.userid,
        userTitle: firstPostAuthor.usertitle,
        name: firstPostAuthor.username,
        avatar: firstPostAuthor.avatar,
        rank: firstPostAuthor.rank,
        url: `/members/m-${firstPostAuthor.userid}`
      }
    },
    lastPost: {
      createdAt: new Date(unifiedTopic.lastActivityAt).getTime(),
      url: createUrl(unifiedTopic, unifiedTopic.lastPostId),
      content: unifiedTopic.content,
      author: {
        id: lastPostAuthor.userid,
        userTitle: lastPostAuthor.usertitle,
        name: lastPostAuthor.username,
        avatar: lastPostAuthor.avatar,
        rank: lastPostAuthor.rank,
        url: `/members/m-${lastPostAuthor.userid}`
      }
    },
    forum: {
      url: `f-${unifiedTopic.categoryId}`,
      title: unifiedTopic.categoryName
    },
    featuredImage: unifiedTopic.featuredImage,
    publicLabel: unifiedTopic.publicLabel,
    contentType: unifiedTopic.contentType,
    content_type: unifiedTopic.contentType,
    type: getType(category ? category.type : null, unifiedTopic.contentType),
    isSticky: unifiedTopic.sticky,
    id: unifiedTopic.publicId,
    privateId: unifiedTopic.id,
    url: createUrl(unifiedTopic),
    title: unifiedTopic.title,
    numberOfViews: unifiedTopic.views,
    numberOfReplies: unifiedTopic.replyCount,
    numberOfLikes: numberOfLikes,
    numberOfUnreadReplies: undefined,
    isTopicUnread: true,
    hasCurrentUserLiked: hasCurrentUserLiked,
    hasBestAnswer: unifiedTopic.hasBestAnswer,
    ideaStatus: unifiedTopic.ideaStatus?.name
      ? {
        name: unifiedTopic.ideaStatus?.name,
        backgroundColor: unifiedTopic.ideaStatus?.backgroundColor,
        textColor: unifiedTopic.ideaStatus?.textColor
      }
      : undefined
  }
}

export const getServerSideProps: GetServerSideProps<CategoryAndContentStreamConfig, Props> = async (ctx, config) => {
  return await ctx.cache(`category-and-topic-stream_${JSON.stringify(config)}_${ctx.userId}`, async (): Promise<Props> => {
    const parentCategories = await prepCategories(
      ctx,
      await getCategories(ctx, [
        LegacyCategoryType.default,
        LegacyCategoryType.news,
        LegacyCategoryType.ideation
      ])
    )
    const unified = new Unified(ctx)
    const maxResults = 10

    const unifiedUnansweredQuestions = await Promise.all((await unified.findByQuery({
      page: 1,
      pageSize: maxResults,
      orderBy: 'lastActivityAt',
      contentType: ['question'],
      hasBestAnswer: false
    })).map(async unifiedUnansweredQuestion => await createTopicInfoFromUnifiedTopic(unifiedUnansweredQuestion, unified, ctx)))

    const unansweredQuestions: UnansweredTopicsProps = {
      tab: 'Help Others',
      requestUrl: '/widget/topics',
      query: {
        sort: 'lastPostDate',
        sortOrder: 'DESC',
        minReplies: 0,
        maxResults: 10,
        contentType: 'question',
        hasBestAnswer: false
      },
      attachmentCdn: ctx.config.config.params.uploadCdn || '',
      topics: unifiedUnansweredQuestions
    }

    const unifiedRecentlyActiveTopics = await Promise.all((await unified.findByQuery({
      page: 1,
      pageSize: maxResults,
      orderBy: 'lastActivityAt'
    })).map(async unifiedRecentlyActiveTopic => await createTopicInfoFromUnifiedTopic(unifiedRecentlyActiveTopic, unified, ctx)))

    const recentlyActiveTopics: RecentlyActiveTopicsProps = {
      tab: 'Recently Active',
      requestUrl: '/search/recentTopics',
      query: {
        sort: 'lastPostDate',
        sortOrder: 'DESC',
        minReplies: 0,
        maxResults: 10
      },
      attachmentCdn: ctx.config.config.params.uploadCdn || '',
      topics: unifiedRecentlyActiveTopics
    }

    return {
      ...config,
      parentCategories: parentCategories,
      collapsed: null,
      displayUnreadPostCount: false,
      unAnsweredTopics: unansweredQuestions,
      recentlyActiveTopics: recentlyActiveTopics
    }
  })
}

class CategoryAndTopicsStream extends Component<Props, State> {
  private readonly STORAGE_POSTFIX = 'tabs-helper-homepage'
  private readonly URLS = {
    categories: '#',
    unanswered: '/search/activity/unanswered',
    recent_active: '/search/activity/recent'
  }

  constructor (props: Props) {
    super(props)
    const enabledTabs = props.tabs.filter(tab => tab.enabled)
    const setActiveTab = typeof window === 'undefined' ? 0 : parseInt(window.localStorage.getItem(this.STORAGE_POSTFIX) ?? '1') - 1
    this.state = {
      tabs: enabledTabs,
      activeTab: setActiveTab >= enabledTabs.length ? 0 : setActiveTab,
      unreadTopicsAndRepliesCount: null
    }
    this.handleChange = this.handleChange.bind(this)
  }

  handleChange (idx: number) {
    this.setState({ activeTab: idx }, () => {
      if (typeof window === 'undefined') return

      window.localStorage.setItem(this.STORAGE_POSTFIX, (this.state.activeTab + 1).toString())
    })
  }

  async componentDidMount () {
    const topicIds: string[] = this.props.parentCategories.reduce((newList: string[], parentCategory) => (
      newList.concat(parentCategory.subforums.map(category => category.id.toString()))
    ), [])

    if (this.props.displayUnreadPostCount) {
      try {
        const { data } = await axios.get('/topic/unreadPostDataInReadableForums', {
          params: {
            topicIds
          }
        })
        this.setState({ unreadTopicsAndRepliesCount: data })
      } catch (e) {
        this.setState({ unreadTopicsAndRepliesCount: null })
      }
    }
  }

  render () {
    const { title, subtitle, backgroundColor, backgroundColorDisplay } = this.props
    const { tabs, activeTab } = this.state
    const categoriesTabIdx = tabs.findIndex(tab => tab.phrase_suffix === 'categories')

    const COMPONENTS: {
      [key: string]: ComponentChildren
    } = {
      categories: <Categories
        parentCategories={this.props.parentCategories}
        collapsed={this.props.collapsed ?? {}}
        displayUnreadPostCount={this.props.displayUnreadPostCount}
        unreadTopicsAndRepliesCount={this.state.unreadTopicsAndRepliesCount}
        isDefaultCollapsed={tabs[categoriesTabIdx]?.collapsed || false}
                  />,
      unanswered: <UnansweredTopics {...this.props.unAnsweredTopics} />,
      recent_active: <RecentlyActiveTopics {...this.props.recentlyActiveTopics} />
    }

    return (
      <WidgetContainer
        config={{
          title,
          subtitle,
          backgroundColor,
          backgroundColorDisplay
        }}
        widgetName='category-and-topic-stream'
      >
        <div className={classnames('box', 'pagebox', 'box--unbox')}>
          <div className='box__content'>
            <div className={classnames('module', 'widget--tabs', 'tabs', 'qa-widget-tabs')}>
              <nav className='sections__nav sections__nav--accordion-S'>
                <div className='tabs--nav__container'>
                  <div className='tabs--nav'>
                    <ul className='tabs--navigation qa-tabs-menu'>
                      {
                        tabs.map((tab, idx) => (
                          <li key={tab.phrase_suffix} className={classnames('tabs__item', 'tabs__item--M', 'qa-tab')} onClick={() => this.handleChange(idx)}>
                            <a
                              href={this.URLS[tab.phrase_suffix]}
                              onClick={(e) => e.preventDefault()}
                              className={`qa-link-tab js-tab ${tab.class}${activeTab === idx ? ' is-active' : ''}`}
                            >{tab.phrase || <Phrase p={`tab.navigation.${(tab.phrase_suffix || '').replace(' ', '_')}`} />}</a>
                          </li>
                        ))
                      }
                    </ul>
                  </div>
                </div>
                <div className='tabs--navigation'>
                  {
                    tabs.map((tab, idx) => (
                      <div className='tabs qa-tabs' key={tab.phrase}>
                        <div className={`js-content js-content-1 qa-tab-isVisible js-content-${idx + 1}${activeTab !== idx ? ' is-hidden--loose' : ''}`}>{COMPONENTS[tab.phrase_suffix]}</div>
                      </div>
                    ))
                  }
                  <div className='tabs qa-tabs' />
                </div>
              </nav>
            </div>
          </div>
        </div>
      </WidgetContainer>
    )
  }
}

export default CategoryAndTopicsStream
